import { g as getRoomType } from './get-room-type.js';
import { t as translateDate } from './translate-date.js';
import './index2.js';

/**
 * **MyGuestCare**
 *
 * @exports bs_371
 * @category engines
 */
function factory({ window, document, thn }) {
    const engine = {};
    let priceUpdate;
    /**
       * @memberOf module:bs_371
       * @see {@link is_user_logged}
       *
       * @param {object} ctx - The context object.
       */
    engine.isUserLogged = function isUserLogged(ctx) {
        const isLogged = thn.isVisible(document.querySelector('#beLoginBtn'));
        return isLogged;
    };
    /**
       * @memberOf module:bs_371
       * @this thn.widgets.price
       * @see {@link Price#observe_dom}
       *
       * @param {object} ctx - The context object.
       */
    engine.priceObserveDom = function priceObserveDom(ctx) {
        const containerEl = document.querySelector('#productscontainer');
        if (containerEl && !priceUpdate) {
            priceUpdate = thn.debounce(() => {
                const loaderEl = containerEl.querySelector('p.loader');
                if (!loaderEl) {
                    thn.widgets.price.update();
                }
            }, 500);
            thn.observeDom(containerEl, priceUpdate, {
                childList: true,
            });
            thn.pageUnload(() => {
                priceUpdate.cancel();
                thn.stopObserveDom(containerEl, priceUpdate);
                priceUpdate = null;
            });
        }
    };
    /*
          The rooms are inside a container with the class "product-tratments" but the cancellation conditions
          are in a separate container with the id "product-rates" so you have to check if it is cancellable
          outside each room by accessing from the parent container(room).
          */
    const checkRefundable = (room, refundableId) => !!room.querySelector(`${refundableId} div.penali p:not([class*=penalty-nocancellation])`);
    /**
      *
      * @memberOf module:bs_371
      *
      */
    const roomDetailsParseData = (roomsEl = '') => roomsEl.map((room) => {
        const roomName = room.querySelector('.product-name')?.textContent;
        const roomType = getRoomType(roomName);
        const roomPrices = [...room.querySelectorAll('.product-treatment')].map((rate) => {
            const ratePrice = thn.parseNumber(rate.querySelector('.treatment-finalprice .price')?.textContent);
            const refundableId = rate.parentElement?.id?.replace('product-tratments', '#product-rates');
            const rateName = rate.querySelector('.treatment-name')?.textContent?.trim();
            // All of these icon class include breakfast
            const hasBreakfast = !!rate.querySelector('span.icon-treat-breakfast, span.icon-treat-fullboard, span.icon-treat-halfboard ,span.icon-treat-allinclusive');
            return {
                id: '',
                name: rateName,
                totalNight: ratePrice,
                breakfast: hasBreakfast,
                refundable: checkRefundable(room, refundableId),
            };
        });
        return {
            id: room?.id || '',
            name: roomName,
            adults: thn.parseNumber(room.querySelector('.item-occupation')?.textContent),
            children: 0,
            type: roomType,
            prices: roomPrices,
        };
    });
    /**
       * @memberOf module:bs_371
       * @this thn.widgets.price
       * @see {@link Price#set_data}
       *
       * @param {object} ctx - The context object.
       */
    engine.priceParseData = function priceParseData(ctx) {
        const widgetData = {};
        // Dates
        const dateBeginVal = document.querySelector('[name="arrivo"]')?.value;
        const dateEndVal = document.querySelector('[name="partenza"]')?.value;
        let nights;
        if (dateBeginVal && dateEndVal) {
            widgetData.dateBegin = dateBeginVal.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$2/$1');
            widgetData.dateEnd = dateEndVal.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$2/$1');
            nights = thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd);
        }
        // Guests, Adults & Children
        const roomsListEl = [...document.querySelectorAll('.list-products .item-product')].filter((roomEl) => thn.isVisible(roomEl));
        const roomsSelected = [...document.querySelectorAll('.sectionroomchoix')];
        const roomsInfo = roomsSelected.map((room, index) => {
            const prices = [...room.querySelectorAll('.treatment-finalprice strong')].map((price) => thn.parseNumber(price.textContent));
            if (prices.length) {
                return {
                    adults: thn.parseNumber(document.querySelector(`#adulti${index + 1}`)?.value) || 2,
                    children: 0,
                    childrenAges: [],
                    price: Math.min(...prices),
                };
            }
            return false;
        }).filter(Boolean);
        roomsInfo.sort((a, b) => a.price - b.price);
        // Prices & Currency || No Rooms
        const currencyEl = thn.parseCurrency(document.querySelector('.treatment-finalprice strong')?.textContent);
        if (roomsInfo.length && currencyEl && nights) {
            widgetData.price = roomsInfo[0].price / nights;
            widgetData.currency = currencyEl;
            widgetData.roomsDispo = roomsInfo;
            widgetData.roomDetails = roomDetailsParseData(roomsListEl);
            widgetData.set = true;
        }
        else if (!roomsInfo.length) {
            widgetData.noRooms = true;
        }
        return widgetData;
    };
    /**
       * @memberOf module:bs_371
       * @this thn.widgets.booking
       * @see {@link Booking#set_data}
       *
       * @param {object} ctx - The context object.
       */
    engine.bookingParseData = function bookingParseData(ctx) {
        // This BE does not provide a booking ID as it sends it directly to your email address
        const widgetData = {};
        const dayBeginText = document.querySelector('.wrapper-date-checkin .date-day-number')?.textContent;
        const monthBeginText = document.querySelector('.wrapper-date-checkin .date-month')?.textContent;
        const yearBeginText = document.querySelector('.wrapper-date-checkin .date-year')?.textContent;
        const dayEndText = document.querySelector('.wrapper-date-checkout .date-day-number')?.textContent;
        const monthEndText = document.querySelector('.wrapper-date-checkout .date-month')?.textContent;
        const yearEndText = document.querySelector('.wrapper-date-checkout .date-year')?.textContent;
        const dateBeginFormatted = translateDate(`${dayBeginText} ${monthBeginText} ${yearBeginText}`, thn.data.language || 'en');
        const dateEndFormatted = translateDate(`${dayEndText} ${monthEndText} ${yearEndText}`, thn.data.language || 'en');
        if (dateBeginFormatted && dateEndFormatted) {
            widgetData.dateBegin = dateBeginFormatted;
            widgetData.dateEnd = dateEndFormatted;
        }
        const priceEl = document.querySelector('.prezzo.price span')?.textContent;
        widgetData.adults = thn.parseNumber(document.querySelector('.product-description .item-occupation > span')?.getAttribute('title'));
        widgetData.currency = thn.parseCurrency(priceEl);
        widgetData.price = thn.parseNumber(priceEl);
        return widgetData;
    };
    return engine;
}

export { factory };
